import { useContext, useEffect, useMemo, useRef, useState } from "react";
import ContextStates from "../context/ContextStates";
import axios from "axios";
import ReactToPrint from "react-to-print";
import { FaPrint } from "react-icons/fa";
import { Url } from "./Url";
import Select from "react-select";

// ================= Helpers combos =================
const num = (v) => (typeof v === "number" ? v : Number.parseFloat(String(v || 0))) || 0;

const buildProductMaps = (productos = []) => {
    const byId = {};
    const byCode = {};
    for (const p of productos) {
        byId[p.id] = p;
        byCode[String(p.codigo)] = p;
    }
    return { byId, byCode };
};

// Suma cantidades por numerocombo (A+B+…)
const buildQtyByCombo = (items = [], productById = {}, productByCode = {}) => {
    const map = {};
    for (const it of items) {
        const master =
            (it.id && productById[it.id]) ||
            (it.codigo && productByCode[String(it.codigo)]) ||
            null;
        const nc = master?.numerocombo;
        if (!nc) continue;
        map[nc] = (map[nc] || 0) + (Number(it.cantidad) || 0);
    }
    return map;
};

const unitFromProductComboTiers = (base, productComboTiers, groupQty) => {
    let unit = num(base);
    const tiers = Array.isArray(productComboTiers)
        ? productComboTiers.slice().sort((a, b) => a.cantidadmin - b.cantidadmin)
        : [];
    for (const t of tiers) {
        if (groupQty >= Number(t.cantidadmin)) unit = num(t.precio_unitario);
        else break;
    }
    return unit;
};

// Precio unitario efectivo para un renglón dada la suma del combo
const unitByCombo = (masterProduct, selfQty, qtyByCombo) => {
    const base = num(masterProduct?.precio ?? masterProduct?.precio_base ?? 0);
    if (masterProduct?.numerocombo && masterProduct?.precios_combo?.length) {
        const gq = qtyByCombo[masterProduct.numerocombo] || 0;
        return unitFromProductComboTiers(base, masterProduct.precios_combo, gq);
    }
    return base;
};

const subtotalByCombo = (masterProduct, qty, qtyByCombo) => {
    const q = Number(qty) || 0;
    if (q <= 0) return 0;
    const u = unitByCombo(masterProduct, q, qtyByCombo);
    return u * q;
};

// ==================================================

const FinalizarVenta = () => {
    const [total, setTotal] = useState(0);
    const [cliente, setCliente] = useState(0);
    const [fecha, setFecha] = useState("");
    const [hora, setHora] = useState("");
    const [nombre, setNombre] = useState("");
    const [razonSocial, setRazonSocial] = useState("");
    const [fechaInicio, setFechaInicio] = useState("");
    const [medios, setMedios] = useState([]);
    const [medioPago, setMedioPago] = useState("1");
    const [medio1, setMedio1] = useState("1");
    const [medio2, setMedio2] = useState("4");
    const [importe1, setImporte1] = useState("");
    const [importe2, setImporte2] = useState("");
    const [efectivo, setEfectivo] = useState("");
    const [finalizar, setFinalizar] = useState(true);
    const [procesando, setProcesando] = useState(false);
    const [datosFactura, setDatosFactura] = useState("");
    const [finVenta, setFinVenta] = useState(false);
    const [habilitado, setHabilitado] = useState(true);
    const [auxVenta, setAuxVenta] = useState([]);
    const [auxTotal, setAuxTotal] = useState(0);
    const [descuentos, setDescuentos] = useState([]);

    const {
        venta = [],
        productos = [],
        tipoPrecio,
        setVenta,
        setFormView,
        setViewOpen,
        modify,
        setModify,
        idUsuario,
        clientes,
        setClientes,
        setFormPrev,
        puntoVenta,
    } = useContext(ContextStates);

    const componentRef = useRef();

    // ======= Medios de pago split =======
    useEffect(() => {
        if (medioPago === "7") {
            if (importe1 === "") {
                setFinalizar(false);
            } else {
                let auxCalc = 0;
                if (num(importe1) > num(total)) {
                    setFinalizar(false);
                    setImporte2("");
                } else {
                    if (medio1 === "3") {
                        auxCalc = total - num(importe1) + num(importe1) * 0.2;
                    } else {
                        auxCalc = total - num(importe1);
                        if (medio2 === "3") {
                            auxCalc = auxCalc * 1.2;
                        }
                    }
                    setImporte2(auxCalc);
                    setFinalizar(true);
                }
            }
        }
    }, [medioPago, importe1, medio1, medio2, total]);

    // ================== COMBOS: mapas + cantidades por combo ==================
    const { byId: productById, byCode: productByCode } = useMemo(
        () => buildProductMaps(productos),
        [productos]
    );

    const qtyByCombo = useMemo(
        () => buildQtyByCombo(venta, productById, productByCode),
        [venta, productById, productByCode]
    );

    // Ítems enriquecidos con precio/subtotal por combo (para total, impresión y envío)
    const itemsCombo = useMemo(() => {
        return (venta || []).map((it) => {
            const master =
                (it.id && productById[it.id]) ||
                (it.codigo && productByCode[String(it.codigo)]) ||
                null;

            if (!master) {
                // Fallback si no encontramos el producto maestro
                const base =
                    num(it.preciopromocion && (it.tipopromo === "1" || it.tipopromo === "2"))
                        ? it.preciopromocion
                        : it.precio;
                const u = num(base);
                const sub = u * num(it.cantidad);
                return {
                    ...it,
                    numerocombo: it.numerocombo ?? null,
                    precio_unitario_final: u,
                    subtotal_combo: sub,
                    _base_sin_combo: u,
                };
            }

            const u = unitByCombo(master, it.cantidad, qtyByCombo);
            const sub = u * (Number(it.cantidad) || 0);
            const baseSinCombo = num(master.precio ?? master.precio_base ?? it.precio ?? 0);

            return {
                ...it,
                numerocombo: master.numerocombo ?? null,
                precio_unitario_final: u,
                subtotal_combo: sub,
                _base_sin_combo: baseSinCombo,
            };
        });
    }, [venta, productByCode, productById, qtyByCombo]);

    // Total y descuentos por combo
    useEffect(() => {
        let acc = 0;
        const desc = [];
        for (const it of itemsCombo) {
            acc += it.subtotal_combo;
            // descuento mostrado = (precio_base * qty) - subtotal_combo (solo si hay combo y mejora el precio)
            const baseTotal = num(it._base_sin_combo) * num(it.cantidad);
            const delta = baseTotal - it.subtotal_combo;
            if (delta > 0.0001) {
                desc.push({
                    id: it.id,
                    descripcion: it.descripcion,
                    monto: delta,
                });
            }
        }
        setTotal(acc);
        setDescuentos(desc);
    }, [itemsCombo]);

    // ================== Fetch varios ==================
    useEffect(() => {
        const getMedios = async () => {
            try {
                const { data } = await axios.post(Url + "api/listmediospago.php", new FormData());
                setMedios(Array.isArray(data?.results) ? data.results : []);
            } catch (e) {
                console.log(e);
            }
        };
        getMedios();
    }, []);

    useEffect(() => {
        const consultaClientes = async () => {
            const formData = new FormData();
            formData.append("idusuario", idUsuario);
            formData.append("filtro", 0);
            formData.append("busqueda", "");
            try {
                const { data } = await axios.post(Url + "api/listClientes.php", formData);
                setClientes(Array.isArray(data?.results) ? data.results : []);
            } catch (e) {
                console.log(e);
            }
        };
        consultaClientes();
    }, [modify, idUsuario, setClientes]);

    // ================== Factura AFIP (sin cambios) ==================
    const facturaAfip = async () => {
        if (total > 0 && medioPago === "5") {
            setProcesando(true);
            const formData = new FormData();
            formData.append("total", String(total));
            try {
                const response = await axios.post(Url + "api/factura.php", formData);
                setProcesando(false);
                setDatosFactura(response?.data?.results?.cae || "");
            } catch (e) {
                console.log(e);
            }
        }
    };

    // ================== Finalizar (envío con importes por combo) ==================
    const finalizarVenta = async () => {
        if (!habilitado || !finalizar) return;

        setHabilitado(false);

        // tipoprecio legado (lo dejo tal cual)
        let tPrecio = 0;
        if (tipoPrecio === 2) tPrecio = 1;
        else if (tipoPrecio === 3) tPrecio = 2;

        // Venta “combo” con importes fijados (para el backend)
        const ventaComboPayload = itemsCombo.map((it) => ({
            id: it.id,
            codigo: it.codigo,
            cantidad: Number(it.cantidad) || 0,
            numerocombo: it.numerocombo ?? null,
            precio_unitario_final: Number(it.precio_unitario_final) || 0,
            subtotal: Number(it.subtotal_combo) || 0,
        }));

        const formData = new FormData();
        formData.append("venta", JSON.stringify(venta)); // legacy (si tu finVenta.php lo usa)
        formData.append("venta_combo", JSON.stringify(ventaComboPayload)); // 👈 NUEVO, con precios por combo
        formData.append("mediopago", medioPago);
        formData.append("idusuario", idUsuario);
        formData.append("cliente", cliente);
        formData.append("total", String(total));
        formData.append("puntoventa", puntoVenta);
        formData.append("tipoprecio", String(tPrecio));
        formData.append("envases", String(0));
        formData.append("descuentos", JSON.stringify(descuentos));
        formData.append("medio1", medio1);
        formData.append("medio2", medio2);
        formData.append("importe1", String(importe1 || 0));

        try {
            const { data } = await axios.post(Url + "api/finVenta.php", formData);
            console.log(data)
            // esperar { results: { fecha, hora, nombre, razonsocial, fechainicio } }
            setFecha(data?.results?.fecha || "");
            setHora(data?.results?.hora || "");
            setNombre(data?.results?.nombre || "");
            setRazonSocial(data?.results?.razonsocial || "");
            setFechaInicio(data?.results?.fechainicio || "");

            setAuxVenta(itemsCombo);  // 👈 imprimimos lo que cobramos de verdad
            setAuxTotal(total);

            setVenta([]);
            setFinVenta(true);

            setModify((x) => (Number(x) || 0) + 1);
            setDatosFactura("");
        } catch (e) {
            console.log(e);
        } finally {
            setHabilitado(true);
        }
    };

    const handleSelect = (item) => setCliente(item.id);
    const handleAdd = () => {
        setFormPrev(2);
        setFormView(30);
    };

    // Para impresión: misma lógica, ya está en auxVenta (itemsCombo)
    //const componentRef = useRef();

    return (
        <div className="w-full mx-auto">
            {!finVenta ? (
                <div className="w-full sm:w-10/12 mx-auto">
                    <div className="w-full mx-auto">
                        <p className="text-center bg-orange-200 p-4 rounded-lg text-3xl sm:text-5xl font-light">
                            Ésta venta ${num(total).toLocaleString("es-AR", { maximumFractionDigits: 2 })}
                        </p>
                    </div>

                    <div className="w-full sm:flex">
                        <div className="bg-gray-50 rounded-lg p-1 sm:p-5 sm:w-1/2">
                            {Array.isArray(medios) &&
                                medios.map((item) => (
                                    <div key={item.id} className="radio">
                                        <label>
                                            <input
                                                type="radio"
                                                name="medioPago"
                                                value={item.id}
                                                checked={medioPago === item.id}
                                                onChange={() => setMedioPago(item.id)}
                                                className="mr-5"
                                            />
                                            {item.descripcion}
                                        </label>
                                    </div>
                                ))}
                        </div>
                    </div>

                    <div className="w-full">
                        <div className="w-full mt-5 mb-10 bg-gray-50 rounded-lg p-5">
                            <div className="sm:items-start">
                                <Select
                                    options={clientes}
                                    placeholder="Seleccione Cliente"
                                    onChange={(value) => handleSelect(value)}
                                    name="sCliente"
                                />
                                <button
                                    type="button"
                                    className="p-3 bg-orange-500 text-white uppercase font-semibold rounded"
                                    onClick={handleAdd}
                                >
                                    Agregar Cliente
                                </button>
                            </div>
                        </div>

                        {medioPago === "1" && (
                            <div className="w-full mt-5 mb-10 bg-blue-200 rounded-lg p-1 sm:p-5">
                                <div className="sm:items-start">
                                    <label className="block text-gray-800 text-2xl uppercase font-bold" htmlFor="efectivo">
                                        Efectivo
                                    </label>
                                    <input
                                        type="number"
                                        className="p-2 rounded border border-gray-400 w-full"
                                        value={efectivo}
                                        id="efectivo"
                                        onChange={(e) => setEfectivo(e.target.value)}
                                        autoFocus
                                    />
                                </div>
                                <p className="text-4xl text-center font-bold mt-5">
                                    Cambio: $
                                    {num(efectivo) >= num(total) &&
                                        (num(efectivo) - num(total)).toLocaleString("es-AR", { maximumFractionDigits: 2 })}
                                </p>
                            </div>
                        )}

                        {medioPago === "5" ? (
                            <div className="w-full mt-5 mb-10 bg-gray-50 rounded-lg p-5">
                                <div className="sm:items-start">
                                    {datosFactura ? (
                                        <p className="bg-green-300 text-green-800 border border-green-800 rounded-xl p-4 w-full">
                                            Se ha generado la factura con CAE {datosFactura}
                                        </p>
                                    ) : !procesando ? (
                                        <button
                                            type="button"
                                            className="w-full p-3 bg-blue-500 text-white uppercase font-semibold rounded"
                                            onClick={facturaAfip}
                                        >
                                            Generar Factura Electrónica
                                        </button>
                                    ) : (
                                        <p className="bg-gray-300 text-gray-800 border border-gray-800 rounded-xl p-4 w-full">
                                            Aguarde mientras se genera la factura, por favor
                                        </p>
                                    )}
                                </div>
                            </div>
                        ) : (
                            medioPago === "7" && (
                                <>
                                    <div className="w-full mt-5 bg-blue-50 rounded-lg p-5">
                                        <p className="text-xl font-bold">Medio de Pago 1</p>
                                        <div className="rounded-lg p-5 sm:w-1/2">
                                            {Array.isArray(medios) &&
                                                medios
                                                    .filter((m) => m.id === "1")
                                                    .map((item) => (
                                                        <div key={item.id} className="radio">
                                                            <label>
                                                                <input
                                                                    type="radio"
                                                                    name="medioPago1"
                                                                    value={item.id}
                                                                    checked={medio1 === item.id}
                                                                    onChange={() => setMedio1(item.id)}
                                                                    className="mr-5"
                                                                />
                                                                {item.descripcion}
                                                            </label>
                                                        </div>
                                                    ))}
                                            <label className="block text-gray-800" htmlFor="importe1">
                                                Importe Medio de Pago 1
                                            </label>
                                            <input
                                                type="text"
                                                className={`p-2 rounded border border-gray-400 w-full ${num(importe1) > num(total) ? "bg-red-300" : "bg-white"
                                                    }`}
                                                value={importe1}
                                                id="importe1"
                                                onChange={(e) => setImporte1(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="w-full mt-5 mb-10 bg-blue-50 rounded-lg p-5">
                                        <p className="text-xl font-bold">Medio de Pago 2</p>
                                        <div className="mt-2 rounded-lg p-5 sm:w-1/2">
                                            {Array.isArray(medios) &&
                                                medios
                                                    .filter((m) => m.id === "4")
                                                    .map((item) => (
                                                        <div key={item.id} className="radio">
                                                            <label>
                                                                <input
                                                                    type="radio"
                                                                    name="medioPago2"
                                                                    value={item.id}
                                                                    checked={medio2 === item.id}
                                                                    onChange={() => setMedio2(item.id)}
                                                                    className="mr-5"
                                                                />
                                                                {item.descripcion}
                                                            </label>
                                                        </div>
                                                    ))}
                                            <label className="block text-gray-800" htmlFor="importe2">
                                                Importe Medio de Pago 2
                                            </label>
                                            <input
                                                type="text"
                                                className="p-2 rounded border border-gray-400 w-full"
                                                value={importe2}
                                                id="importe2"
                                                onChange={(e) => setImporte2(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                </>
                            )
                        )}
                    </div>

                    <div className="w-2/3 md:w-1/3 mb-5 mx-auto">
                        <button
                            type="button"
                            className={`w-full p-3 text-white uppercase font-semibold rounded ${habilitado && finalizar ? "bg-green-500" : "bg-cyan-400"
                                }`}
                            onClick={finalizarVenta}
                            disabled={!habilitado || !finalizar}
                        >
                            {habilitado ? "Finalizar venta" : "Aguarde un instante por favor"}
                        </button>
                    </div>
                </div>
            ) : (
                // ================== Comprobante (usa itemsCombo que guardamos en auxVenta) ==================
                <div className="w-10/12 mx-auto">
                    <div className="print:px-4 print:text-xs print:h-svh" ref={componentRef}>
                        <div className="w-full mx-auto">
                            <p className="text-center text-2xl font-bold print:text-lg">{nombre}</p>
                            <p className="text-left print:pl-2 rounded-lg text-xl print:text-xs">{`Razón Social: ${razonSocial}`}</p>
                            <p className="text-left print:pl-2 rounded-lg text-xl print:text-xs">{`Inicio de Act.: ${fechaInicio}`}</p>
                            <div className="w-full mt-3 mb-7">
                                <div className="w-full print:pl-2">
                                    <p className="text-left">Fecha {fecha}</p>
                                </div>
                                <div className="w-full print:pl-2">
                                    <p className="text-left">Hora {hora}</p>
                                </div>
                            </div>
                        </div>

                        {Array.isArray(auxVenta) &&
                            auxVenta.map((item, index) => (
                                <div key={index} className="w-full">
                                    <div className="w-full print:pl-2 print:pr-10">
                                        <p className="text-left print:text-xs">
                                            {`${Number(item.cantidad).toFixed(2)} x ${item.codigo} - ${item.descripcion}`}
                                        </p>
                                    </div>
                                    <div className="w-full print:pr-10">
                                        <p className="text-right print:text-xs">
                                            $
                                            {Number(item.subtotal_combo ?? item.subtotal ?? 0).toLocaleString("es-AR", {
                                                maximumFractionDigits: 2,
                                            })}
                                        </p>
                                    </div>
                                </div>
                            ))}

                        <div className="w-full print:pl-2">
                            <p className="text-left font-bold uppercase text-3xl print:text-sm">Total</p>
                        </div>
                        <div className="w-full print:pr-10">
                            <p className="text-right font-bold uppercase text-3xl print:text-sm">
                                ${Number(auxTotal).toLocaleString("es-AR", { maximumFractionDigits: 2 })}
                            </p>
                        </div>

                        {!!descuentos?.length && (
                            <div className="w-full print:pl-2">
                                <p className="text-left font-bold uppercase text-3xl print:text-sm">Descuentos</p>
                            </div>
                        )}
                        {descuentos.map((d, i) => (
                            <div key={i} className="w-full">
                                <div className="w-full print:pl-2 print:pr-10">
                                    <p className="text-left print:text-xs">{d.descripcion}</p>
                                </div>
                                <div className="w-full print:pr-10">
                                    <p className="text-right print:text-xs">
                                        ${Number(d.monto).toLocaleString("es-AR", { maximumFractionDigits: 2 })}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="w-full rounded-lg px-3 flex justify-center pb-5">
                        <ReactToPrint
                            trigger={() => (
                                <button
                                    type="button"
                                    className="w-2/3 sm:w-1/4 flex shadow-md p-3 ml-3 bg-indigo-500 text-white hover:bg-indigo-400 transition-all uppercase font-bold rounded-lg"
                                >
                                    <FaPrint className="mx-auto mt-1" />
                                    <p className="mx-auto text-center">Imprimir Comprobante</p>
                                </button>
                            )}
                            content={() => componentRef.current}
                        />
                        <button
                            type="button"
                            className="w-1/3 sm:w-1/6 shadow-md p-3 ml-3 bg-gray-500 text-white hover:bg-gray-400 transition-all uppercase font-bold rounded-lg"
                            onClick={() => setViewOpen(false)}
                        >
                            Salir
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FinalizarVenta;
